import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { HttpClient, HttpClientRequest } from "./HttpClientTypes";

const inFlightGets = new Map<string, number>();

/** In-flight GETs, optionally only those whose endpoint matches `match`. */
export const getInFlightGetCount = (match?: RegExp): number => {
  let total = 0;
  inFlightGets.forEach((count, endpoint) => {
    if (!match || match.test(endpoint)) {
      total += count;
    }
  });
  return total;
};

export default class HttpClientImpl<P> implements HttpClient<P> {
  private requestTimeout: number;
  private axiosInstance: AxiosInstance;

  /**
   * constructor for HTTP client.
   */
  public constructor(baseURL = "", requestTimeout = 250000) {
    this.requestTimeout = requestTimeout;
    this.axiosInstance = axios.create({
      baseURL,
      timeout: this.requestTimeout,
    });

    this.axiosInstance.interceptors.response.use((response) => response);
  }

  public async post(request: HttpClientRequest): Promise<P> {
    const { endpoint, body, ...requestConfig } = request;
    return this.axiosInstance
      .post<P>(endpoint, body, requestConfig)
      .then((response) => response.data);
  }

  public async put(request: HttpClientRequest): Promise<P> {
    const { endpoint, body, ...requestConfig } = request;
    return this.axiosInstance
      .put<P>(endpoint, body, requestConfig)
      .then((response) => response.data);
  }

  public async patch(request: HttpClientRequest): Promise<P> {
    const { endpoint, body, ...requestConfig } = request;
    return this.axiosInstance
      .patch<P>(endpoint, body, requestConfig)
      .then((response) => response.data);
  }

  public async get(request: HttpClientRequest): Promise<P> {
    const requestConfig: AxiosRequestConfig = {
      headers: request.headers,
    };
    const { endpoint } = request;

    if (request.params) {
      requestConfig.params = request.params;
    }

    inFlightGets.set(endpoint, (inFlightGets.get(endpoint) ?? 0) + 1);
    try {
      return await this.axiosInstance
        .get<P>(endpoint, requestConfig)
        .then((response) => response.data);
    } finally {
      const remaining = (inFlightGets.get(endpoint) ?? 1) - 1;
      if (remaining > 0) {
        inFlightGets.set(endpoint, remaining);
      } else {
        inFlightGets.delete(endpoint);
      }
    }
  }
  public async delete(request: HttpClientRequest): Promise<P> {
    const requestConfig: AxiosRequestConfig = {
      headers: request.headers,
    };
    const { endpoint } = request;

    if (request.params) {
      requestConfig.params = request.params;
    }

    return this.axiosInstance
      .delete<P>(endpoint, requestConfig)
      .then((response) => response.data);
  }
}
